import { Component, effect, EffectRef, ElementRef, inject, OnDestroy, Signal, signal, untracked, ViewChild, WritableSignal } from '@angular/core';
import { ButtonComponent } from '../../../../shared/button/button.component';
import { MenuService } from '../../../../../services/components/menu.service';
import { SearchService } from '../../../../../services/components/search.service';
import { AppViews } from '../../../../../models/enums/dvm/DvmViews.enum';
import { DvmService } from '../../../../../services/dvm.service';
import { Menu, MenuKeys } from '@models/interfaces/components/Menu.interface';
import anime from 'animejs/lib/anime.es.js';
import { LayoutService } from '@services/layout.service';

@Component({
    selector: 'app-menu-buttons',
    standalone: true,
    imports: [
        ButtonComponent
    ],
    templateUrl: './menu-buttons.component.html',
    styleUrl: './menu-buttons.component.css'
})
export class MenuButtonsComponent implements OnDestroy {

  // VIEW CHILDREN
  @ViewChild('menuButtons', {static: false})
  public readonly  menuButtons!:  ElementRef<HTMLDivElement>;

  // SERVICES
  private readonly MenuService:   MenuService               = inject(MenuService);
  private readonly searchService: SearchService             = inject(SearchService);
  private readonly layoutService:  LayoutService            = inject(LayoutService);

  // GLOBAL STATE
  public readonly  view:          Signal<AppViews|null>     = inject(DvmService).view;
  public readonly  isMobile:      Signal<boolean>           = this.layoutService.isMobile;
  private readonly menuData:      Signal<Menu[]>            = this.MenuService.menusList;

  // GETTERS
  public get selectedMenu(): Menu | undefined { return this.MenuService.menuSelected; }

  // SEARCH STATE
  private readonly searchActive:  Signal<boolean|null>      = this.searchService.searchActive;
  private readonly duration:      number                    = this.searchService.duration;


  // ANIMATION STATE
  protected showMenus:            WritableSignal<boolean>   = signal(false);
  protected disabled:             WritableSignal<boolean>   = signal(false);
  protected timer:                ReturnType<typeof setTimeout> | null  = null;
  private hideTimer:              ReturnType<typeof setTimeout> | null  = null;

  // // EFFECTS
  public onToggleSearch:          EffectRef                 = effect((onCleanup): void => {

      onCleanup((): void => {
        if(this.timer) clearTimeout(this.timer);
        this.clearHideTimer();
      });

      const isSearchActive: boolean | null = this.searchActive();
      const isSeatMap:      boolean        = untracked(this.view) === AppViews.Seatmap;

      if (isSearchActive === null || isSeatMap) return;

      if(isSearchActive){

        untracked(() => {
          this.toggleMenuVisibility(false);
          if(this.MenuService.showContainer()) this.MenuService.closeMenuContainer(); // Si está abierto, emulamos que pulsamos el mismo menú para cerrarlo.
        });

        this.clearHideTimer();
        this.hideTimer = window.setTimeout((): void => {
          this.showMenus.set(false);
          this.hideTimer = null;
        }, this.duration);

      }
      else {

        if(this.timer) clearTimeout(this.timer);

        this.showMenus.set(true);

        this.timer = window.setTimeout((): void => this.toggleMenuVisibility(true), this.duration);

      }


  });

  public onViewChange:            EffectRef                 = effect((): void => {

        const view: AppViews | null = this.view();

        if(view === null) return;

        if(this.timer) clearTimeout(this.timer);

        untracked(() => {
          switch(view) {

            case AppViews.Topview:
            case AppViews.Main:

              this.toggleMenuVisibility(true);
              break;

            case AppViews.Seatmap:
            case AppViews.Pano:
              this.toggleMenuVisibility(false);
              break;

          }
        });


  });


  // LIFECYCLE HOOKS
  public ngOnDestroy(): void {
    if(this.timer) clearTimeout(this.timer);
    this.clearHideTimer();
  }


  // GETTERS
  public get menus(): Menu[] {

    return [...this.menuData()].sort((a, b) => a.order - b.order);

  }


  // METHODS
  public toggleMenu(key:MenuKeys): void {

    this.MenuService.toggleMenuContainer(key);
    
  }

  public isMenuActive(key: MenuKeys): boolean {
    return (
      this.MenuService.menuSelected?.id === key || 
      this.MenuService.hasMenuSelectedItem(key)
    );
  }

  private toggleMenuVisibility(show: boolean): void {
    const menuButtons: HTMLDivElement | undefined = this.menuButtons?.nativeElement;
    if(!menuButtons) return;

    if(!this.showMenus() && show) this.showMenus.set(true);

    this.disabled.set(!show);

    const targets: HTMLCollection = menuButtons.children;

    anime({
      targets:    targets,
      duration:   show ? this.duration + 150 : this.duration,
      easing:     'easeInOutSine',
      opacity:    show ? 1 : 0,
      translateY: show ? 0 : this.isMobile() ? 20 : -20,
      delay:      anime.stagger(100),
      complete:   (): void => { this.showMenus.set(show); }
    });



  }

  private clearHideTimer(): void {
    if(!this.hideTimer) return;
    clearTimeout(this.hideTimer);
    this.hideTimer = null;
  }

}
